import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Warehouse } from "lucide-react";

// Roles that work primarily on the warehouse floor. A one-time nudge suggests Floor View
// so a brand-new tech doesn't have to already know it exists. After it's shown once
// (server flag floor_nudge_seen), it never appears again.
const WAREHOUSE_ROLES = ["Technician"];

export function FloorNudge() {
  const { user, setSurface, refresh } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const show = !!user && WAREHOUSE_ROLES.includes(user.role) &&
    !user.floor_nudge_seen && user.preferred_surface !== "floor" && !dismissed;

  const markSeen = async () => { try { await api.post("/me/floor-nudge-seen"); } catch { /* ignore */ } };
  const notNow = async () => { setDismissed(true); await markSeen(); refresh?.(); };
  const tryFloor = async () => { setDismissed(true); await markSeen(); await setSurface("floor"); navigate("/floor"); };

  if (!show) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) notNow(); }}>
      <DialogContent data-testid="floor-nudge" className="max-w-md">
        <DialogHeader>
          <div className="w-14 h-14 rounded-xl bg-[#B01E28] flex items-center justify-center mb-2">
            <Warehouse className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="text-2xl font-black tracking-tight">Working on the floor?</DialogTitle>
          <DialogDescription className="text-base">
            Floor View is a big-button, scan-first screen built for a tablet and work gloves.
            Scan any tag — part, truck, or bin — and act on it right there. You can switch back to the
            full desk view anytime.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button data-testid="floor-nudge-dismiss" variant="outline" onClick={notNow}>Not now</Button>
          <Button data-testid="floor-nudge-accept" className="bg-[#B01E28] hover:bg-[#8B1820]" onClick={tryFloor}>
            Try Floor View
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
